// @/src/core/constants/defaults.ts

import type { Categoria, MetodoPago, MetodoIngreso, EmpresaMensajeria } from '@/core/types';

// --- ADMINS (fuente única: shared/billing/admins.ts, compartida con Cloud Functions) ---
export { ADMIN_EMAILS } from '@shared/billing/admins';

/**
 * @file src/core/constants/defaults.ts
 * @description Define los datos por defecto para la inicialización de la app.
 * Estos arrays se utilizan para poblar la configuración de un nuevo usuario.
 * No incluyen timestamps, ya que estos se añaden dinámicamente en el servicio
 * al momento de la creación para reflejar la fecha exacta del evento.
 */

// --- EGRESOS DEL NEGOCIO ---
export const DEFAULT_CATEGORIAS_EGRESOS_NEGOCIO: Omit<Categoria, 'timestamp'>[] = [
    {
        id: 'cat_marketing_def_neg',
        nombre: 'Marketing',
        subcategorias: [
            { id: 'sub_fb_ads_def_neg', nombre: 'Facebook Ads' },
            { id: 'sub_tiktok_ads_def_neg', nombre: 'Tik Tok Ads' },
            { id: 'sub_google_ads_def_neg', nombre: 'Google Ads' },
            { id: 'sub_pub_trad_def_neg', nombre: 'Publicidad Tradicional' },
            { id: 'sub_otros_mkt_def_neg', nombre: 'Otros' }
        ],
        isDefault: true,
        tipo: 'negocio'
    },
    {
        id: 'cat_compra_inv_def_neg',
        nombre: 'Compra de inventario',
        subcategorias: [
            { id: 'sub_centrum_mujer_def_neg', nombre: 'Frascos Centrum Mujer' },
            { id: 'sub_centrum_bal_def_neg', nombre: 'Frascos Centrum Balance' }
        ],
        isDefault: true,
        tipo: 'negocio'
    }
];

export const DEFAULT_METODOS_PAGO_EGRESOS_NEGOCIO: Omit<MetodoPago, 'timestamp'>[] = [
    { id: 'mp_efectivo_def_neg', nombre: 'Efectivo', isDefault: true, tipo: 'negocio' },
    { id: 'mp_tarjeta_def_neg', nombre: 'Tarjeta', isDefault: true, tipo: 'negocio' },
    { id: 'mp_transferencia_def_neg', nombre: 'Transferencia', isDefault: true, tipo: 'negocio' },
    { id: 'mp_otros_def_neg', nombre: 'Otros', isDefault: true, tipo: 'negocio' }
];

// --- GASTOS PERSONALES ---
export const DEFAULT_CATEGORIAS_GASTOS_PERSONALES: Omit<Categoria, 'timestamp'>[] = [
    {
        id: 'cat_vivienda_def_per',
        nombre: 'Vivienda',
        subcategorias: [
            { id: 'sub_alquiler_def_per', nombre: 'Alquiler' },
            { id: 'sub_agua_def_per', nombre: 'Agua' },
            { id: 'sub_luz_def_per', nombre: 'Luz' },
            { id: 'sub_gas_def_per', nombre: 'Gas' },
            { id: 'sub_inttel_def_per', nombre: 'Internet y Teléfono' },
            { id: 'sub_mantrep_viv_def_per', nombre: 'Mantenimiento y Reparaciones' },
            { id: 'sub_basura_def_per', nombre: 'Servicio de Basura' },
            { id: 'sub_otros_viv_def_per', nombre: 'Otros' }
        ],
        isDefault: true,
        tipo: 'personal'
    },
    {
        id: 'cat_emergencias_imprev_def_per',
        nombre: 'Emergencias e Imprevistos',
        subcategorias: [
            { id: 'sub_gastos_med_no_plan_def_per', nombre: 'Gastos Médicos no Planificados' },
            { id: 'sub_reparaciones_urg_def_per', nombre: 'Reparaciones Urgentes' },
            { id: 'sub_accidente_veh_def_per', nombre: 'Accidente vehicular' },
            { id: 'sub_otros_imprev_def_per', nombre: 'Otros Imprevistos' }
        ],
        isDefault: true,
        tipo: 'personal'
    },
];

export const DEFAULT_METODOS_PAGO_GASTOS_PERSONALES: Omit<MetodoPago, 'timestamp'>[] = [
    { id: 'mp_efectivo_def_per', nombre: 'Efectivo', isDefault: true, tipo: 'personal' },
    { id: 'mp_tarjeta_def_per', nombre: 'Tarjeta', isDefault: true, tipo: 'personal' },
    { id: 'mp_transferencia_def_per', nombre: 'Transferencia', isDefault: true, tipo: 'personal' },
    { id: 'mp_otros_def_per', nombre: 'Otros', isDefault: true, tipo: 'personal' }
];

// --- OTROS INGRESOS ---
export const DEFAULT_METODOS_INGRESO_OTROS: Omit<MetodoIngreso, 'timestamp'>[] = [
    { id: 'mio_efectivo_def', nombre: 'Efectivo', isDefault: true, tipo: 'otro_ingreso', orden: 0 },
    { id: 'mio_transferencia_def', nombre: 'Transferencia Bancaria', isDefault: true, tipo: 'otro_ingreso', orden: 1 },
    { id: 'mio_cheque_def', nombre: 'Cheque', isDefault: false, tipo: 'otro_ingreso', orden: 2 },
    { id: 'mio_paypal_def', nombre: 'PayPal', isDefault: false, tipo: 'otro_ingreso', orden: 3 },
    { id: 'mio_otro_def', nombre: 'Otro', isDefault: false, tipo: 'otro_ingreso', orden: 4 }
];

// --- EMPRESAS DE MENSAJERÍA GUATEMALA ---
export const DEFAULT_EMPRESAS_MENSAJERIA: Omit<EmpresaMensajeria, 'timestamp'>[] = [
    {
        id: 'em_cargo_expreso',
        nombre: 'Cargo Expreso',
        codigoEmpresa: 'cargo_expreso',
        badgeText: 'NACIONAL / COD',
        descripcion: 'Cobertura nacional y cobro contra entrega',
        logoUrl: '/assets/images/couriers/cargo-expreso.svg',
        enlaceWeb: 'https://cargoexpreso.com',
        codActivo: true,
        orden: 0,
        isPrincipal: false
    },
    {
        id: 'em_forza',
        nombre: 'Forza Delivery Express',
        codigoEmpresa: 'forza',
        badgeText: 'NACIONAL / COD',
        descripcion: 'Servicio de paquetería y logística nacional',
        logoUrl: '/assets/images/couriers/forza.svg',
        enlaceWeb: 'https://forzadelivery.com',
        codActivo: true,
        orden: 1,
        isPrincipal: false
    },
    {
        id: 'em_guatex',
        nombre: 'Guatex',
        codigoEmpresa: 'guatex',
        badgeText: 'NACIONAL',
        descripcion: 'Cobertura departamental y envíos a todo el país',
        logoUrl: '/assets/images/couriers/guatex.svg',
        enlaceWeb: 'https://guatex.com',
        codActivo: true,
        orden: 2,
        isPrincipal: true
    },
    {
        id: 'em_fedex',
        nombre: 'FedEx Express',
        codigoEmpresa: 'fedex',
        badgeText: 'EXPRESS / INTL',
        descripcion: 'Envíos expresos nacionales e internacionales',
        logoUrl: '/assets/images/couriers/fedex.svg',
        enlaceWeb: 'https://fedex.com',
        codActivo: false,
        orden: 3,
        isPrincipal: false
    },
    {
        id: 'em_dhl',
        nombre: 'DHL Express',
        codigoEmpresa: 'dhl',
        badgeText: 'INTERNACIONAL',
        descripcion: 'Servicio de paquetería y logística global',
        logoUrl: '/assets/images/couriers/dhl.svg',
        enlaceWeb: 'https://dhl.com',
        codActivo: false,
        orden: 4,
        isPrincipal: false
    },
    {
        id: 'em_mensajeria_propia',
        nombre: 'Otras Mensajerías',
        codigoEmpresa: 'mensajeria_propia',
        badgeText: 'PERSONALIZADO',
        descripcion: 'Otras empresas de paquetería o mensajerías locales',
        logoUrl: '/assets/images/couriers/propia.svg',
        codActivo: true,
        orden: 5,
        isPrincipal: false
    }
];